"use client";

import {
  Box,
  Button,
  Flex,
  HStack,
  Icon,
  Text,
  VStack,
} from "@chakra-ui/react";
import { useTranslations, useLocale } from "next-intl";
import { usePathname, useRouter } from "next/navigation";
import { PublicHeader } from "@/_components/layout/PublicHeader";
import { NextLink } from "@/_components/ui/NextLink";
import { FiChevronDown } from "react-icons/fi";
import {
  HiOutlineUserGroup,
  HiOutlineUser,
  HiOutlineOfficeBuilding,
} from "react-icons/hi";
import {
  TbFileDescription,
  TbShieldCheck,
  TbCreditCardRefund,
} from "react-icons/tb";
import { useState, useRef, useCallback } from "react";
import { IconType } from "react-icons";
import { HELP_CENTER_IDS } from "../_constants/help-center.ids";

interface NavChild {
  icon: IconType;
  label: string;
  description: string;
  href: string;
  isLink?: boolean;
}

interface NavItem {
  id: string;
  label: string;
  href: string;
  children: NavChild[];
}

export function HelpCenterHeader() {
  const t = useTranslations("landing");
  const locale = useLocale();
  const pathname = usePathname();

  const navItems: NavItem[] = [
    {
      id: "about",
      label: t("help_center.tabs.about"),
      href: `/${locale}/help-center/about`,
      children: [
        {
          icon: HiOutlineUserGroup,
          label: t("subnav.about.company.story.title"),
          description: t("subnav.about.company.story.description"),
          href: `/${locale}/help-center/about#about-story`,
          isLink: true,
        },
        {
          icon: HiOutlineUser,
          label: t("subnav.about.company.vision.title"),
          description: t("subnav.about.company.vision.description"),
          href: `/${locale}/help-center/about#about-vision`,
          isLink: true,
        },
        {
          icon: HiOutlineOfficeBuilding,
          label: t("subnav.about.company.roadmap.title"),
          description: t("subnav.about.company.roadmap.description"),
          href: `/${locale}/help-center/about#about-roadmap`,
          isLink: true,
        },
      ],
    },
    {
      id: "guide",
      label: "Guide",
      href: `/${locale}/help-center/guide`,
      children: [
        {
          icon: HiOutlineUserGroup,
          label: "Getting Started",
          description: "Overview and core product capabilities",
          href: `/${locale}/help-center/guide#guide-intro`,
          isLink: true,
        },
        {
          icon: HiOutlineUser,
          label: "Showcase",
          description: "Video walkthrough and platform impact",
          href: `/${locale}/help-center/guide#guide-showcase`,
          isLink: true,
        },
      ],
    },
    {
      id: "policy",
      label: t("help_center.tabs.policy"),
      href: `/${locale}/help-center/policy`,
      children: [
        {
          icon: TbFileDescription,
          label: t("subnav.about.legal.terms.title"),
          description: t("subnav.about.legal.terms.description"),
          href: `/${locale}/help-center/terms-of-service`,
          isLink: true,
        },
        {
          icon: TbShieldCheck,
          label: t("subnav.about.legal.privacy.title"),
          description: t("subnav.about.legal.privacy.description"),
          href: `/${locale}/help-center/privacy-policy`,
          isLink: true,
        },
        {
          icon: TbCreditCardRefund,
          label: t("subnav.about.legal.refund.title"),
          description: t("subnav.about.legal.refund.description"),
          href: `/${locale}/help-center/refund-policy`,
          isLink: true,
        },
      ],
    },
  ];

  const desktopNav = (
    <HStack id={HELP_CENTER_IDS.header.desktopNav} gap={1}>
      {navItems.map((item) => (
        <NavDropdown
          key={item.id}
          item={item}
          isActive={pathname.startsWith(item.href)}
        />
      ))}
    </HStack>
  );

  const mobileNav = (onClose: () => void) =>
    navItems.map((item) => (
      <MobileNavAccordion
        key={item.id}
        item={item}
        isActive={pathname.startsWith(item.href)}
        onClose={onClose}
      />
    ));

  return (
    <Box id={HELP_CENTER_IDS.header.root}>
      <PublicHeader
        screenName={t("help_center.title")}
        navbar={desktopNav}
        mobileNavbar={mobileNav}
        showBookDemo={false}
      />
    </Box>
  );
}

/* ─── Desktop dropdown ─── */

function NavDropdown({ item, isActive }: { item: NavItem; isActive: boolean }) {
  const [open, setOpen] = useState(false);
  const hoverTimeout = useRef<NodeJS.Timeout | null>(null);
  const router = useRouter();

  const handleMouseEnter = useCallback(() => {
    if (hoverTimeout.current) clearTimeout(hoverTimeout.current);
    setOpen(true);
  }, []);

  const handleMouseLeave = useCallback(() => {
    hoverTimeout.current = setTimeout(() => setOpen(false), 150);
  }, []);

  const handleChildClick = (child: NavChild) => {
    setOpen(false);

    router.push(child.href);
  };

  return (
    <Box
      id={HELP_CENTER_IDS.header.desktopItem(item.id)}
      position="relative"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <Button
        id={HELP_CENTER_IDS.header.desktopItemButton(item.id)}
        visual="none-style"
        size="sm"
        fontSize="13px"
        fontWeight={isActive ? "600" : "400"}
        color={isActive ? "primary.600" : "neutral.500"}
        bg={isActive ? "primary.50" : "transparent"}
        borderRadius="full"
        px={4}
        _hover={{ color: "primary.600", bg: "primary.50" }}
        onClick={() => router.push(item.href)}
      >
        <HStack gap={1}>
          <Text>{item.label}</Text>
          <Box
            display="inline-flex"
            transition="transform 0.25s cubic-bezier(.4,1.5,.5,1)"
            transform={open ? "rotate(180deg)" : "rotate(0deg)"}
          >
            <Icon as={FiChevronDown} boxSize={3.5} />
          </Box>
        </HStack>
      </Button>

      <Box
        id={HELP_CENTER_IDS.header.desktopDropdown(item.id)}
        position="absolute"
        top="100%"
        left="50%"
        transform="translateX(-50%)"
        pt={2}
        opacity={open ? 1 : 0}
        visibility={open ? "visible" : "hidden"}
        transition="all 0.2s ease"
        zIndex={1400}
        pointerEvents={open ? "auto" : "none"}
      >
        <Box
          bg="white"
          borderRadius="14px"
          boxShadow="0 12px 40px rgba(0,0,0,0.1), 0 2px 10px rgba(0,0,0,0.06)"
          border="1px solid"
          borderColor="blackAlpha.100"
          minW="280px"
          overflow="hidden"
          p={2}
        >
          <VStack
            id={HELP_CENTER_IDS.header.desktopDropdownList(item.id)}
            align="stretch"
            gap={0.5}
          >
            {item.children.map((child, index) => (
              <SubNavItemCard
                key={child.href}
                child={child}
                isLink={child.isLink}
                debugId={HELP_CENTER_IDS.header.desktopSubitem(
                  item.id,
                  index + 1,
                )}
                linkId={HELP_CENTER_IDS.header.desktopSubitemLink(
                  item.id,
                  index + 1,
                )}
                boxId={HELP_CENTER_IDS.header.desktopSubitemBox(
                  item.id,
                  index + 1,
                )}
                onClick={() => handleChildClick(child)}
              />
            ))}
          </VStack>
        </Box>
      </Box>
    </Box>
  );
}

function SubNavItemCard({
  child,
  isLink,
  onClick,
  debugId,
  linkId,
  boxId,
}: {
  child: NavChild;
  isLink?: boolean;
  onClick: () => void;
  debugId: string;
  linkId: string;
  boxId: string;
}) {
  const content = (
    <HStack
      id={debugId}
      gap={3}
      p={2.5}
      borderRadius="10px"
      transition="all 0.15s ease"
      _hover={{ bg: "neutral.50" }}
      align="start"
      cursor="pointer"
    >
      <Flex
        w="34px"
        h="34px"
        borderRadius="8px"
        bg="primary.50"
        align="center"
        justify="center"
        flexShrink={0}
        mt={0.5}
      >
        <Icon as={child.icon} boxSize={4} color="primary.500" />
      </Flex>
      <Box>
        <Text
          fontSize="13px"
          fontWeight="500"
          color="neutral.800"
          lineHeight="1.3"
          mb={0.5}
        >
          {child.label}
        </Text>
        <Text fontSize="11px" color="neutral.400" lineHeight="1.4">
          {child.description}
        </Text>
      </Box>
    </HStack>
  );

  if (isLink) {
    return (
      <NextLink
        id={linkId}
        href={child.href}
        _hover={{ textDecoration: "none" }}
        onClick={(e) => {
          e.preventDefault();
          onClick();
        }}
      >
        {content}
      </NextLink>
    );
  }

  return (
    <Box id={boxId} onClick={onClick}>
      {content}
    </Box>
  );
}

/* ─── Mobile accordion ─── */

function MobileNavAccordion({
  item,
  isActive,
  onClose,
}: {
  item: NavItem;
  isActive: boolean;
  onClose: () => void;
}) {
  const [expanded, setExpanded] = useState(isActive);
  const router = useRouter();

  const handleChildClick = (child: NavChild) => {
    onClose();
    router.push(child.href);
  };

  return (
    <Box id={HELP_CENTER_IDS.header.mobileItem(item.id)} w="full">
      <HStack
        id={HELP_CENTER_IDS.header.mobileItemTrigger(item.id)}
        gap={1}
        cursor="pointer"
        onClick={() => {
          if (!isActive) {
            router.push(item.href);
            return;
          }
          setExpanded((prev) => !prev);
        }}
        py={1}
      >
        <Text
          fontWeight={isActive ? "medium" : "light"}
          fontFamily="heading"
          textStyle="xl"
          color={isActive ? "primary.600" : "neutral.700"}
        >
          {item.label}
        </Text>
        <Box
          display="inline-flex"
          transition="transform 0.25s cubic-bezier(.4,1.5,.5,1)"
          transform={expanded ? "rotate(180deg)" : "rotate(0deg)"}
        >
          <Icon as={FiChevronDown} boxSize={4} color="neutral.500" />
        </Box>
      </HStack>

      <Box
        id={HELP_CENTER_IDS.header.mobileItemPanel(item.id)}
        overflow="hidden"
        maxH={expanded ? "600px" : "0"}
        opacity={expanded ? 1 : 0}
        transition="all 0.3s ease"
      >
        <VStack
          id={HELP_CENTER_IDS.header.mobileItemList(item.id)}
          align="stretch"
          gap={1}
          pl={2}
          pt={2}
          pb={3}
        >
          {item.children.map((child, index) => (
            <HStack
              id={HELP_CENTER_IDS.header.mobileSubitem(item.id, index + 1)}
              key={child.href}
              gap={3}
              p={2}
              borderRadius="8px"
              _hover={{ bg: "neutral.50" }}
              cursor="pointer"
              onClick={() => handleChildClick(child)}
            >
              <Flex
                w="30px"
                h="30px"
                borderRadius="6px"
                bg="primary.50"
                align="center"
                justify="center"
                flexShrink={0}
              >
                <Icon as={child.icon} boxSize={3.5} color="primary.500" />
              </Flex>
              <Box>
                <Text fontSize="sm" fontWeight="500" color="neutral.700">
                  {child.label}
                </Text>
                <Text fontSize="xs" color="neutral.400">
                  {child.description}
                </Text>
              </Box>
            </HStack>
          ))}
        </VStack>
      </Box>
    </Box>
  );
}
